import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lock, ShieldCheck } from "lucide-react";

const TONE = {
  orange:  { on: "border-orange-400 bg-orange-50",   off: "border-neutral-200 bg-neutral-50", accent: "accent-orange-600" },
  emerald: { on: "border-emerald-400 bg-emerald-50", off: "border-slate-300 bg-slate-100",    accent: "accent-emerald-600" },
  violet:  { on: "border-violet-400 bg-violet-50",   off: "border-slate-300 bg-slate-100",    accent: "accent-violet-600" },
};

// Checkbox em card colorido (flag booleana; undefined/null conta como true).
function CheckCard({ checked, onChange, testId, title, desc, tone = "orange", className = "" }) {
  const t = TONE[tone];
  return (
    <label className={`flex items-start gap-3 cursor-pointer border-2 rounded-lg p-3 transition ${checked ? t.on : t.off} ${className}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className={`mt-1 ${t.accent} w-5 h-5 shrink-0`} data-testid={testId} />
      <div>
        <div className="font-bold text-sm">{title}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </label>
  );
}

function Section({ title, titleClass, children, intro }) {
  return (
    <div className="md:col-span-2 mt-2 border-t pt-4">
      <div className={`text-sm font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${titleClass}`}>{title}</div>
      {intro && <p className="text-xs text-muted-foreground mb-3">{intro}</p>}
      {children}
    </div>
  );
}

export function EmpresaValidacaoSection({ f, set }) {
  return (
    <Section title="Validação no App do Entregador" titleClass="text-orange-800"
      intro="Selecione quais comprovações são obrigatórias ao concluir/remarcar/cancelar uma entrega. Você pode ativar todas, algumas ou nenhuma.">
      <div className="grid md:grid-cols-3 gap-3">
        <CheckCard checked={f.require_foto_evidencia !== false} onChange={(v) => set({ require_foto_evidencia: v })} testId="emp-require-foto"
          title="📸 Foto de evidência" desc="Entregador tira uma foto pela câmera (sem galeria) antes de finalizar." />
        <CheckCard checked={f.require_codigo_4dig !== false} onChange={(v) => set({ require_codigo_4dig: v })} testId="emp-require-codigo"
          title="🔢 Código de 4 dígitos" desc="Entregador confirma os 4 últimos dígitos do celular do cliente ao concluir." />
        <CheckCard checked={f.require_gps_capture !== false} onChange={(v) => set({ require_gps_capture: v })} testId="emp-require-gps"
          title="📍 GPS obrigatório" desc="Coordenadas do celular precisam ser capturadas ao finalizar (para heatmap e rastreabilidade)." />
      </div>
      <p className="text-xs text-muted-foreground mt-2">⚠️ Desativar essas validações reduz a segurança de comprovação da entrega. Recomendado manter ativas.</p>
    </Section>
  );
}

// Licenciamento (fev/2026): SA-only, controla bloqueio de acesso de todos os usuários da empresa.
export function EmpresaLicencaSection({ f, set, editing }) {
  const ativa = f.licenca_ativa !== false;
  return (
    <Section title={<><ShieldCheck className="w-4 h-4" />Licenciamento</>} titleClass="text-purple-800"
      intro="Controla o acesso desta empresa ao Dashboard e App Mobile. Desativar ou vencer a licença bloqueia TODOS os usuários da empresa (exceto Super Admin).">
      <div className="grid md:grid-cols-3 gap-3">
        <CheckCard tone="emerald" checked={ativa} onChange={(v) => set({ licenca_ativa: v })} testId="emp-licenca-ativa"
          title={ativa ? "Licença Ativa" : "Licença Inativa"}
          desc={ativa ? "Empresa acessa normalmente todas as telas." : "🔒 Todos os usuários serão bloqueados imediatamente."} />
        <div className="md:col-span-2">
          <Label className="text-xs">Data de expiração <span className="text-muted-foreground">(opcional)</span></Label>
          <Input type="date" value={f.licenca_expira_em || ""} onChange={(e) => set({ licenca_expira_em: e.target.value })} data-testid="emp-licenca-expira" />
          <p className="text-[10px] text-muted-foreground mt-1">Se preenchido, o acesso será bloqueado automaticamente a partir do dia seguinte à data escolhida.</p>
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Mensagem personalizada na tela de bloqueio <span className="text-muted-foreground">(opcional)</span></Label>
          <Textarea rows={2} value={f.licenca_observacao || ""} onChange={(e) => set({ licenca_observacao: e.target.value })}
            placeholder="Ex: Sua mensalidade venceu. Regularize para reativar o acesso." data-testid="emp-licenca-observacao" />
        </div>
      </div>
      {editing && !ativa && (
        <p className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1" data-testid="licenca-warn-inactive">
          <Lock className="w-3.5 h-3.5" />Atenção: ao salvar, todos os usuários desta empresa perderão acesso imediatamente.
        </p>
      )}
    </Section>
  );
}

// Rastreamento GPS em tempo real: flag SOBERANA do SA (empresa só ativa no painel dela se liberado aqui).
export function EmpresaRastreamentoSection({ f, set }) {
  const on = f.rastreamento_gps_liberado !== false;
  return (
    <Section title="📡 Rastreamento em Tempo Real" titleClass="text-violet-800"
      intro={<>Controle soberano do Super Admin. Quando <b>desativado</b>, a empresa não conseguirá usar rastreio nem que ative no painel dela — o app mobile não sobe serviço de localização em background e o menu <b>Monitoramento</b> some do dashboard do cliente.</>}>
      <CheckCard tone="violet" checked={on} onChange={(v) => set({ rastreamento_gps_liberado: v })} testId="emp-rastreamento-liberado"
        title={on ? "Liberar Rastreamento GPS em Tempo Real" : "Rastreamento GPS bloqueado"}
        desc={on ? "Empresa poderá ativar o feature no painel dela quando quiser." : "🔒 A empresa não vê o menu Monitoramento e o mobile não envia posição GPS, mesmo se tentar ativar no painel."} />
    </Section>
  );
}

// Backup automático (até 3 horários BRT; vazio = global) + raio do Centro para sugestão de região (Iter 108).
export function EmpresaBackupRegiaoSection({ f, set }) {
  return (
    <>
      <Section title="🗄️ Backup Automático (até 3× por dia)" titleClass="text-sky-800"
        intro={<>Configure até 3 horários por dia (BRT) para o backup automático rodar. Deixe em branco pra usar o horário padrão global (definido no <b>Cofre de Backups</b>).</>}>
        <div className="grid md:grid-cols-3 gap-3">
          {[1, 2, 3].map((n) => (
            <div key={n}>
              <Label className="text-xs">Backup {n} <span className="text-muted-foreground">(opcional)</span></Label>
              <Input type="time" value={f[`backup_horario_${n}`] || ""} onChange={(e) => set({ [`backup_horario_${n}`]: e.target.value })}
                data-testid={`emp-backup-horario-${n}`} className="font-mono" />
            </div>
          ))}
        </div>
      </Section>
      <Section title="🧭 Regiões pela posição" titleClass="text-sky-800">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <Label className="text-xs">Raio do Centro (km) <span className="text-muted-foreground">(padrão 2)</span></Label>
            <Input type="number" inputMode="decimal" min={0.5} max={20} step={0.5} value={f.regiao_raio_centro_km ?? ""} placeholder="2"
              onChange={(e) => set({ regiao_raio_centro_km: e.target.value })} data-testid="emp-raio-centro" className="font-mono" />
          </div>
          <p className="text-xs text-muted-foreground flex-1 min-w-[220px]">Bairros até esse raio do centro da cidade são sugeridos como "Centro"; fora disso, a direção define Zona Norte/Sul/Leste/Oeste.</p>
        </div>
      </Section>
    </>
  );
}
